import React from "react";

import NoteIcon from "@mui/icons-material/Note";
import { Box, Grid, Typography } from "@mui/material";
import { capitalize, startCase } from "lodash";
import { makeStyles } from "tss-react/mui";

import { EditIconButton } from "@/components/IconButtons";
import { NCIcon } from "@/components/Icons/Icon";
import { graphql } from "@/gql";
import useDialogState from "@/hooks/useDialogState";
import { useFragment } from "@/hooks/useFragment";
import { colors } from "@/theme/overrides/colors";

import EditMedicalHistoryModal from "./components/EditMedicalHistoryModal";
import PregnancyHistory from "./PregnancyHistory";

interface Props {
  clientId: string;
}

const useStyles = makeStyles()({
  section: {
    padding: 24,
    backgroundColor: colors.white,
    width: "100%",
  },
  textValue: {
    padding: "4px 12px",
    display: "inline-block",
  },
  diseases: {
    marginRight: "2px",
    marginBottom: "2px",
  },
  nameHeader: {
    display: "flex",
    alignItems: "center",
    marginBottom: "12px",
  },
  valueContainer: {
    display: "flex",
    flexDirection: "column",
    alignItems: "start",
    marginBottom: "12px",
  },
  diseasesContainer: {
    marginBottom: "12px",
  },
  icon: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    marginRight: 12,
    width: "40px",
    height: "40px",
    borderRadius: "100%",
    backgroundColor: "#E9FBF7",
  },
});

const medicalHistoryFragment = graphql(`
  fragment MedicalHistorySection on Client {
    id
    profile {
      id
      gender
    }
    canHavePregnancyHistory
    medicalHistory {
      ...ClientMedicalHistory
    }
    pregnancyHistory {
      ...ClientPregnancyHistory
    }
  }

  fragment ClientMedicalHistory on MedicalHistory {
    id
    clientId
    diseases
    medications
    medicalNotes
    personalHistory
    familyHistory
    otherMedicalInfo
    createdAt
    updatedAt
  }
`);

const MedicalHistory: React.FC<Props> = ({ clientId }) => {
  const { classes } = useStyles();
  const [client] = useFragment(medicalHistoryFragment, `Client:${clientId}`);
  const gender = client?.profile?.gender;
  const medicalHistory = client?.medicalHistory;
  const pregnancyHistory = client?.pregnancyHistory;
  const personalHistory = client?.medicalHistory?.personalHistory;
  const familyHistory = client?.medicalHistory?.familyHistory;
  const otherMedicalInfo = client?.medicalHistory?.otherMedicalInfo;
  const editDialog = useDialogState();

  return (
    <>
      <Box
        width="100%"
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mt={5}
        mb={3}
      >
        <Typography variant="subtitle2" fontWeight="bold">
          Medical history
        </Typography>
        <Box marginY={3} />
        <EditIconButton onClick={editDialog.show} />
      </Box>
      <section className={classes.section}>
        <Grid container spacing={3}>
          <Grid container item spacing={3} xs={12}>
            {client?.canHavePregnancyHistory && pregnancyHistory?.id && (
              <Grid item xs={4}>
                <PregnancyHistory pregnancyHistoryId={pregnancyHistory.id} />
              </Grid>
            )}

            <Grid item xs={4}>
              <div className={classes.nameHeader}>
                <NCIcon icon="virus-icon" htmlColor={colors.gray} mr="normal" />
                <Typography variant="body1">
                  Ailments/illnesses (if any)
                </Typography>
              </div>
              <div className={classes.diseasesContainer}>
                {medicalHistory?.diseases?.map((e) => (
                  <Typography
                    key={e}
                    variant="body2"
                    color="textSecondary"
                    className={`${classes.textValue} ${classes.diseases}`}
                  >
                    {capitalize(startCase(e))}
                  </Typography>
                ))}
              </div>
              {medicalHistory?.medications && (
                <>
                  <div className={classes.nameHeader}>
                    <NCIcon
                      icon="other-medical-concern"
                      htmlColor={colors.gray}
                      mr="normal"
                    />
                    <Typography variant="body1">Medications</Typography>
                  </div>
                  <div className={classes.valueContainer}>
                    {medicalHistory.medications.map((e) => (
                      <Typography key={e} variant="body2" color="textSecondary">
                        {capitalize(e)}
                      </Typography>
                    ))}
                  </div>
                </>
              )}

              <div className={classes.nameHeader}>
                <div className={classes.icon}>
                  <NoteIcon htmlColor={colors.gray} />
                </div>
                <Typography variant="body1">Notes</Typography>
              </div>
              <div className={classes.valueContainer}>
                <Typography variant="body2" color="textSecondary">
                  {medicalHistory?.medicalNotes}
                </Typography>
              </div>
            </Grid>

            <Grid item xs={4}>
              <div className={classes.nameHeader}>
                <NCIcon
                  icon="other-medical-concern"
                  htmlColor={colors.gray}
                  mr="normal"
                />
                <Typography variant="body1">Other</Typography>
              </div>

              <div className={classes.valueContainer}>
                <Typography variant="body2" color="textSecondary">
                  {otherMedicalInfo ? capitalize(otherMedicalInfo) : "N/A"}
                </Typography>
              </div>

              {personalHistory && (
                <>
                  <div className={classes.nameHeader}>
                    <NCIcon icon="person" htmlColor={colors.gray} mr="normal" />
                    <Typography variant="body1">Personal History</Typography>
                  </div>
                  <div className={classes.valueContainer}>
                    <Typography variant="body2" color="textSecondary">
                      {capitalize(personalHistory)}
                    </Typography>
                  </div>
                </>
              )}

              {familyHistory && (
                <>
                  <div className={classes.nameHeader}>
                    <NCIcon icon="family" htmlColor={colors.gray} mr="normal" />
                    <Typography variant="body1">Family History</Typography>
                  </div>
                  <div className={classes.valueContainer}>
                    <Typography variant="body2" color="textSecondary">
                      {capitalize(familyHistory)}
                    </Typography>
                  </div>
                </>
              )}
            </Grid>
          </Grid>
        </Grid>

        <EditMedicalHistoryModal
          visible={editDialog.shown}
          onClose={editDialog.hide}
          gender={gender}
          medicalHistory={medicalHistory}
          pregnancyHistory={pregnancyHistory}
        />
      </section>
    </>
  );
};

export default MedicalHistory;
